import { type PropsWithChildren } from "react"
import { Button, Card, Stack, StackItem, Typography } from "@octopus-energy/coral/atoms"
import { formatCurrency } from "@oefr/utils/formatter/currency"
import { useTranslations } from "next-intl"
import CustomHead from "@/components/atoms/CustomHead"
import { Page } from "@/components/organisms"
import { type Referral } from "@/features/reward-scheme"
import { NextIntlRichText } from "@/features/translations"
import { onboardingRoutesPaths } from "@/routes/onboarding"
import { type SbReferralStory } from "../../../../types"
import { StoryblokBlokLooper } from "../../../StoryblokBlokLooper"

type ReferralTemplateProps = {
    story: SbReferralStory
    referral: Referral
}

function BoldTertiaryText({ children }: PropsWithChildren) {
    return (
        <Typography
            component="span"
            color="tertiary500"
        >
            <b>{children}</b>
        </Typography>
    )
}

export function ReferralTemplate({ story, referral }: ReferralTemplateProps) {
    const { topBlock, bottomBlock, metaRobots, metadata } = story.content

    const t = useTranslations("ReferralTemplate")

    function withRewardCode(url: string) {
        const rewardParams = new URLSearchParams({ codeParrainage: referral.code })

        return `${url}?${rewardParams.toString()}`
    }

    const onboardingRoute = withRewardCode(onboardingRoutesPaths.meterDetails.resolve())

    return (
        <>
            <CustomHead
                title={metadata?.title}
                description={metadata?.description}
                robots={metaRobots?.join(",")}
            />
            <Page>
                <Stack
                    flexDirection="column"
                    alignItems="center"
                    gap="xxxl"
                >
                    <Stack
                        gap="xl"
                        flexDirection="column"
                        alignItems="center"
                    >
                        <Stack flexDirection="column">
                            <Typography
                                textStyle="h1"
                                color="tertiary500"
                                textAlign="center"
                            >
                                <NextIntlRichText>
                                    {(tags) =>
                                        t.rich("title", {
                                            ...tags,
                                            referrerGivenName: referral.referrerGivenName,
                                        })
                                    }
                                </NextIntlRichText>
                            </Typography>
                            <Typography textAlign="center">
                                <NextIntlRichText>
                                    {(tags) =>
                                        t.rich("subtitle", {
                                            ...tags,
                                            referrerRewardAmount: formatCurrency(
                                                referral.referrerRewardAmount
                                            ),
                                            referredRewardAmount: formatCurrency(
                                                referral.referredRewardAmount
                                            ),
                                        })
                                    }
                                </NextIntlRichText>
                            </Typography>
                        </Stack>
                        <StackItem>
                            <Card>
                                <Stack
                                    fullHeight
                                    gap="lg"
                                    flexDirection="column"
                                    justifyContent="space-between"
                                >
                                    <Typography textAlign="center">
                                        {t.rich("cardReferral.content", {
                                            b: (chunks) => (
                                                <BoldTertiaryText>{chunks}</BoldTertiaryText>
                                            ),
                                            totalRewardAmount: formatCurrency(
                                                referral.referrerRewardAmount +
                                                    referral.referredRewardAmount
                                            ),
                                        })}
                                    </Typography>
                                    <Button href={onboardingRoute}>
                                        {t("cardReferral.callToAction")}
                                    </Button>
                                </Stack>
                            </Card>
                        </StackItem>
                    </Stack>
                    <Stack
                        flexDirection="column"
                        gap="xl"
                    >
                        <StoryblokBlokLooper blocks={topBlock} />
                        <StoryblokBlokLooper blocks={bottomBlock} />
                    </Stack>
                </Stack>
            </Page>
        </>
    )
}

ReferralTemplate.messages = ["ReferralTemplate"] as const
